import React, { useRef, useState, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useConfig } from '../ConfigProvider'
import cloneDeep from 'lodash/cloneDeep'
import FileItem from './FileItem'
import Button from '../Buttons'
import CloseButton from '../CloseButton'
import Notification from '../Notification'
import toast from '../toast'
import { ReactSortable } from 'react-sortablejs'

const filesToArray = (files) => Object.keys(files).map((key) => files[key])

const Upload = React.forwardRef((props, ref) => {
    const {
        accept,
        beforeUpload,
        disabled,
        sortable,
        draggable,
        fileList,
        multiple,
        onChange,
        onChangeSortable,
        onFileRemove,
        showList,
        tip,
        uploadLimit,
        children,
        className,
        field,
        form,
        ...rest
    } = props

    const fileInputField = useRef(null)
    const [files, setFiles] = useState(fileList)
    const [dragOver, setDragOver] = useState(false)
    const { themeColor, primaryColorLevel } = useConfig()

    useEffect(() => {
        setFiles(fileList)
    }, [fileList])

    const triggerMessage = (msg) => {
        toast.push(
            <Notification type="danger" duration={2000}>
                {msg || 'Upload Failed!'}
            </Notification>,
            {
                placement: 'top-center',
            }
        )
    }

    const pushFile = (newFiles, file) => {
        for (let f of newFiles) {
            file.push(f)
        }
        return file
    }

    const addNewFiles = (newFiles) => {
        let file = cloneDeep(files)

        // 如果有設置上傳限制
        if (typeof uploadLimit === 'number' && uploadLimit > 0) {
            // 計算可以新增的檔案數量
            const remainingSlots = uploadLimit - file.length

            if (remainingSlots <= 0) {
                // 如果是單檔上傳，替換現有檔案
                if (uploadLimit === 1) {
                    return filesToArray([newFiles[0]])
                }
                // 如果已達上限，不新增檔案
                return filesToArray(file)
            }

            // 只取可新增的數量
            const allowedNewFiles = newFiles.slice(0, remainingSlots)
            file = [...file, ...allowedNewFiles]
        } else {
            // 無上傳限制，全部新增
            file = [...file, ...newFiles]
        }

        return filesToArray(file)
    }

    const onNewFileUpload = async (e) => {
        const fileList = e.target.files
        const newFiles = Array.from(fileList)

        // 檢查是否超過上傳限制
        if (uploadLimit > 0 && files.length + newFiles.length > uploadLimit) {
            triggerMessage(`最多只能上傳 ${uploadLimit} 個檔案`)
            return
        }

        let result = true

        if (beforeUpload) {
            try {
                result = await Promise.resolve(beforeUpload(newFiles, files))
                if (result === false) {
                    triggerMessage()
                    return
                }

                if (typeof result === 'string' && result.length > 0) {
                    triggerMessage(result)
                    return
                }
            } catch (error) {
                triggerMessage(error?.message || '上傳檢查失敗')
                return
            }
        }

        if (result) {
            let updatedFiles = addNewFiles(newFiles)
            // setFiles(updatedFiles)
            onChange?.(
                updatedFiles,
                files,
                updatedFiles,
                filesToArray(newFiles)
            )
        }
    }

    const removeFile = (fileIndex) => {
        const deletedFileList = files.filter((_, index) => index !== fileIndex)
        setFiles(deletedFileList)
        onFileRemove?.(deletedFileList)
    }

    const triggerUpload = (e) => {
        if (!disabled) {
            fileInputField.current?.click()
        }
        e.stopPropagation()
    }

    const renderChildren = () => {
        if (!draggable && !children) {
            return (
                <Button disabled={disabled} onClick={(e) => e.preventDefault()}>
                    Upload
                </Button>
            )
        }

        if (draggable && !children) {
            return <span>選擇一個檔案或拖放到此處</span>
        }

        return children
    }

    const handleDragLeave = useCallback(() => {
        if (draggable) {
            setDragOver(false)
        }
    }, [draggable])

    const handleDragOver = useCallback(() => {
        if (draggable && !disabled) {
            setDragOver(true)
        }
    }, [draggable, disabled])

    const handleDrop = useCallback(() => {
        if (draggable) {
            setDragOver(false)
        }
    }, [draggable])

    const draggableProp = {
        onDragLeave: handleDragLeave,
        onDragOver: handleDragOver,
        onDrop: handleDrop,
    }

    const draggableEventFeedbackClass = `border-${themeColor}-${primaryColorLevel}`

    const uploadClass = classNames(
        'upload',
        draggable && `upload-draggable`,
        draggable && !disabled && `hover:${draggableEventFeedbackClass}`,
        draggable && disabled && 'disabled',
        dragOver && draggableEventFeedbackClass,
        className
    )

    const uploadInputClass = classNames(
        'upload-input',
        draggable && `draggable`
    )

    const renderFileItems = () =>
        files.map((file, index) => (
            <FileItem
                file={file}
                key={file.name + index}
                className={classNames({
                    'opacity-25': file.loading || !file,
                    'pointer-events-none': file.loading || !file,
                })}
            >
                <CloseButton
                    onClick={() => removeFile(index)}
                    className="upload-file-remove"
                />
            </FileItem>
        ))
    return (
        <>
            <div
                ref={ref}
                className={uploadClass}
                {...(draggable ? draggableProp : { onClick: triggerUpload })}
                {...rest}
            >
                <input
                    className={uploadInputClass}
                    type="file"
                    ref={fileInputField}
                    onChange={onNewFileUpload}
                    disabled={disabled}
                    multiple={multiple}
                    accept={accept}
                    title=""
                    value=""
                    {...field}
                    {...rest}
                ></input>
                {renderChildren()}
            </div>
            {tip}
            {showList && (
                <div className="upload-file-list">
                    {sortable ? (
                        <ReactSortable
                            list={files}
                            setList={(list) => {
                                setFiles(list)
                                if (sortable && onChangeSortable) {
                                    onChangeSortable(list, sortable)
                                }
                            }}
                        >
                            {renderFileItems()}
                        </ReactSortable>
                    ) : (
                        renderFileItems()
                    )}
                </div>
            )}
        </>
    )
})

Upload.propTypes = {
    uploadLimit: PropTypes.number,
    draggable: PropTypes.bool,
    disabled: PropTypes.bool,
    showList: PropTypes.bool,
    multiple: PropTypes.bool,
    accept: PropTypes.string,
    tip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
}

Upload.defaultProps = {
    draggable: false,
    showList: true,
    disabled: false,
    fileList: [],
}

export default Upload
